import type { Client } from '@ujet/websdk-headless';
import type { ChatMessengerElement } from '@/types/chat-messenger';

interface UjetChatMessage {
  $userType?: string;
  content?: string;
}

interface UjetMemberIdentity {
  name?: string;
}

const HANDOFF_ACTIVE_KEY = 'ujet-handoff-active';

let ujetClient: Client | null = null;
let userInputListenerRegistered = false;
let fileSelectListenerRegistered = false;
let sessionResetListenerRegistered = false;
let handoffListenersRegistered = false;
let handoffMessengerSessionId: string | null = null;
let currentMessenger: ChatMessengerElement | null = null;
let sessionResetListener: (() => void) | null = null;
let userInputListener: ((e: Event) => void) | null = null;
let fileSelectListener: ((e: Event) => void) | null = null;
let initPromise: Promise<void> | null = null;

const isBrowser = (): boolean => typeof window !== 'undefined';

const readHandoffActiveFromStorage = (): boolean =>
  isBrowser() && sessionStorage.getItem(HANDOFF_ACTIVE_KEY) === '1';

let handoffActive = readHandoffActiveFromStorage();

const setHandoffActive = (value: boolean): void => {
  handoffActive = value;
  if (!isBrowser()) return;
  if (value) sessionStorage.setItem(HANDOFF_ACTIVE_KEY, '1');
  else sessionStorage.removeItem(HANDOFF_ACTIVE_KEY);
  if (!value) {
    handoffMessengerSessionId = null;
    handoffListenersRegistered = false;
  }
};

interface ChatSdkContext { state?: { sessionId?: string } }
interface ChatSdkGlobal { chatSdk?: { getContext: () => ChatSdkContext } }

const getMessengerSessionId = (): string | null => {
  if (!isBrowser()) return null;

  return (window as unknown as ChatSdkGlobal).chatSdk?.getContext().state?.sessionId ?? null;
};

const showAgentMessage = (
  messenger: ChatMessengerElement,
  content: string,
  sender: string,
): void => {
  messenger.renderCustomCard([ {
    type: 'custom_template',
    name: 'ujet-agent-message',
    payload: { content, sender },
  } ]);
};

const showSystemMessage = (messenger: ChatMessengerElement, content: string): void => {
  messenger.renderCustomCard([ {
    type: 'custom_template',
    name: 'ujet-system-message',
    payload: { content },
  } ]);
};

const endUjetChat = async (): Promise<void> => {
  if (!ujetClient) return;
  try {
    await ujetClient.chat.disconnect();
  } catch {
    /* ignore */
  }
  setHandoffActive(false);
};

const wireHandoffListeners = (messenger: ChatMessengerElement): void => {
  if (!ujetClient || handoffListenersRegistered) return;

  ujetClient.on('chat.message', (message: UjetChatMessage) => {
    if (message.$userType !== 'end_user' && message.content) {
      showAgentMessage(messenger, message.content, 'Agent');
    }
  });

  ujetClient.on('chat.connected', () => {
    showSystemMessage(messenger, 'Connecting to a store representative');
  });

  ujetClient.on('chat.memberJoined', (identity: UjetMemberIdentity) => {
    showSystemMessage(messenger, `${identity?.name || 'Agent'} has joined`);
  });

  ujetClient.on('chat.ended', () => {
    showSystemMessage(messenger, 'The agent has ended the chat');
    setHandoffActive(false);
  });

  handoffListenersRegistered = true;
};

const restoreHandoff = async (messenger: ChatMessengerElement): Promise<void> => {
  if (!ujetClient) return;
  try {
    const chat = await ujetClient.loadOngoingChat();
    if (!chat) {
      setHandoffActive(false);

      return;
    }
    handoffMessengerSessionId = getMessengerSessionId();
    wireHandoffListeners(messenger);
  } catch {
    setHandoffActive(false);
  }
};

const startHandoff = async (messenger: ChatMessengerElement): Promise<void> => {
  if (!ujetClient) throw new Error('UJET client not initialized');
  const handoffMenuId = Number(process.env.NEXT_PUBLIC_CCAI_HANDOFF_MENU_ID ?? '26');
  handoffMessengerSessionId = getMessengerSessionId();
  setHandoffActive(true);
  try {
    wireHandoffListeners(messenger);
    await ujetClient.createChat(handoffMenuId, { lang: 'en' });
  } catch (err) {
    setHandoffActive(false);
    throw err;
  }
};

export interface UjetUserRefValue {
  name: string;
  email: string;
}

export interface UjetUserRef {
  current: UjetUserRefValue | null;
}

export const cleanupUjetHandoff = (): void => {
  if (sessionResetListener) {
    window.removeEventListener('chat-messenger-session-id-set', sessionResetListener);
    sessionResetListener = null;
    sessionResetListenerRegistered = false;
  }
  if (userInputListener) {
    window.removeEventListener('chat-messenger-user-input-entered', userInputListener, true);
    userInputListener = null;
    userInputListenerRegistered = false;
  }
  if (fileSelectListener) {
    window.removeEventListener('chat-messenger-upload-file-selected', fileSelectListener, true);
    fileSelectListener = null;
    fileSelectListenerRegistered = false;
  }
};

export const initUjetClient = (
  messenger: ChatMessengerElement,
  userRef: UjetUserRef,
): Promise<void> => {
  if (currentMessenger && messenger !== currentMessenger) {
    initPromise = null;
    ujetClient = null;
    handoffListenersRegistered = false;
  }
  currentMessenger = messenger;

  if (initPromise) return initPromise;
  if (!isBrowser()) return Promise.resolve();

  const companyId = process.env.NEXT_PUBLIC_CCAI_COMPANY_ID;
  const host = process.env.NEXT_PUBLIC_CCAI_HOST;
  const apiUrl = process.env.NEXT_PUBLIC_CCAI_API_URL;

  if (!companyId || !host || !apiUrl) return Promise.resolve();

  if (!sessionResetListenerRegistered) {
    sessionResetListener = (): void => {
      if (!handoffActive || !handoffMessengerSessionId) return;
      const newId = getMessengerSessionId();
      if (newId && newId !== handoffMessengerSessionId) void endUjetChat();
    };
    window.addEventListener('chat-messenger-session-id-set', sessionResetListener);
    sessionResetListenerRegistered = true;
  }

  initPromise = import('@ujet/websdk-headless')
    .then(({ Client }) => {
      ujetClient = new Client({
        companyId,
        host,
        authenticate: async (): Promise<{ token: string }> => {
          const { name, email } = userRef.current ?? { name: 'Anonymous', email: 'anonymous@example.com' };
          const formData = new FormData();
          formData.append('name', name);
          if (email) formData.append('email', email);
          const resp = await fetch(`${apiUrl}/jwt`, {
            method: 'POST',
            body: formData,
          });
          if (!resp.ok) throw new Error(`JWT fetch failed: ${resp.status}`);
          const token = await resp.text();

          return { token };
        },
      });

      ujetClient.on('ready', () => {
        if (handoffActive && currentMessenger) {
          void restoreHandoff(currentMessenger);
        }
      });

      messenger.registerBackendService({
        isServing: () => handoffActive,
        fetch: () =>
          new Response(
            JSON.stringify({
              outputs: [ {
                diagnosticInfo: {
                  messages: [ { chunks: [ { updatedVariables: {} } ] } ],
                },
              } ],
            }),
            { status: 200, headers: { 'Content-Type': 'application/json' } },
          ),
      });

      if (!userInputListenerRegistered) {
        userInputListener = (e: Event): void => {
          if (!handoffActive || !ujetClient) return;
          const { detail } = e as CustomEvent<{ input?: string }>;
          if (detail?.input) void ujetClient.sendTextMessage(detail.input);
        };
        window.addEventListener('chat-messenger-user-input-entered', userInputListener, true);
        userInputListenerRegistered = true;
      }

      if (!fileSelectListenerRegistered) {
        fileSelectListener = (e: Event): void => {
          if (!handoffActive || !ujetClient) return;
          const { detail } = e as CustomEvent<{ file?: File }>;
          if (detail?.file) void ujetClient.sendFileMessage(detail.file);
        };
        window.addEventListener('chat-messenger-upload-file-selected', fileSelectListener, true);
        fileSelectListenerRegistered = true;
      }
    })
    .catch((err: unknown) => {
      initPromise = null;
      throw err;
    });

  return initPromise;
};

export const initUjetHandoff = async (
  messenger: ChatMessengerElement,
  userRef: UjetUserRef,
): Promise<void> => {
  await initUjetClient(messenger, userRef);
  if (!ujetClient || handoffActive) return;

  await startHandoff(messenger);
};
